import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  approveAdminBilling,
  getAdminBillingTransactions,
} from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import KeyValueTable from "../../components/ui/KeyValueTable";
import AdminLayout from "../../layouts/AdminLayout";
import AdminBlockingModal from "./AdminBlockingModal";
import { getPlatformRole, isAdminRole } from "../../utils/platformRole";
import { handleTableKeyNav } from "../../utils/tableNav";
import normalizeApiError from "../../utils/normalizeApiError";
import SuccessToast from "../../components/ui/SuccessToast";
import useSingleFlight from "../../utils/useSingleFlight";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusTone = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "completed" || value === "success")
    return "bg-emerald-100 text-emerald-700";
  if (value === "pending" || value === "active")
    return "bg-amber-100 text-amber-700";
  if (value === "failed" || value === "error")
    return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-500";
};

export default function AdminBilling() {
  const role = getPlatformRole();
  const canManage = isAdminRole(role);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  const [approvalResponse, setApprovalResponse] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    userId: "",
    amount: "",
    paymentReference: "",
  });
  const [blockingError, setBlockingError] = useState("");
  const [actionLogs, setActionLogs] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const userSelectRef = React.useRef(null);
  const approveTriggerRef = React.useRef(null);
  const closeApproveModal = () => {
    setShowApproveModal(false);
    setApprovalError(null);
    setApprovalResponse(null);
    approveTriggerRef.current?.focus();
  };
  const approvalFlight = useSingleFlight();

  const canGoPrev = page > 1;
  const canGoNext = total ? page * pageSize < total : transactions.length === pageSize;

  const generateIdempotencyKey = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `admin-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAdminBillingTransactions({
        page,
        pageSize,
      });
      setTransactions(response?.items || []);
      setTotal(response?.total ?? null);
      setPage(response?.page || page);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      const code = err?.response?.data?.code;
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        setBlockingError(err?.response?.data?.message || "Credits exhausted.");
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, pageSize]);

  const handleApprove = async () =>
    approvalFlight.run(async () => {
      if (!canManage) return;
      setApprovalLoading(true);
      setApprovalError(null);
      setApprovalResponse(null);
      try {
        const payload = {
          userId: approvalForm.userId,
          amount: Number(approvalForm.amount || 0),
          paymentReference: approvalForm.paymentReference,
          idempotencyKey: generateIdempotencyKey(),
        };
        if (!payload.userId || !payload.amount || !payload.paymentReference) {
          setApprovalError(
            normalizeApiError(
              "Approval requires userId, amount, and paymentReference."
            )
          );
          return;
        }
        const response = await approveAdminBilling(payload, payload.idempotencyKey);
        setApprovalResponse(response || null);
        setActionLogs((prev) => [
          {
            action: "APPROVE_BILLING",
            entity: "billing",
            userId: payload.userId,
            transactionId: selectedTransactionId || null,
            idempotencyKey: payload.idempotencyKey,
            status: "success",
            apiCalls: 1,
          },
          ...prev,
        ]);
      } catch (err) {
        const code = err?.response?.data?.code;
        if (code === "BILLING_CREDITS_EXHAUSTED") {
          setBlockingError(err?.response?.data?.message || "Credits exhausted.");
          return;
        }
        if (code === "INVALID_ENTITY") {
          setApprovalError(
            normalizeApiError({
              code: "INVALID_ENTITY",
              message: "Invalid entity for approval.",
            })
          );
          return;
        }
        if (code === "INVALID_USER_ID") {
          setApprovalError(
            normalizeApiError({
              code: "INVALID_USER_ID",
              message: "Invalid user ID.",
            })
          );
          return;
        }
        setApprovalError(normalizeApiError(err));
        setActionLogs((prev) => [
          {
            action: "APPROVE_BILLING",
            entity: "billing",
            userId: approvalForm.userId,
            transactionId: selectedTransactionId || null,
            idempotencyKey: payload.idempotencyKey,
            status: "error",
            apiCalls: 1,
          },
          ...prev,
        ]);
      } finally {
        setApprovalLoading(false);
      }
    });

  const transactionOptions = useMemo(() => {
    return (transactions || [])
      .filter((tx) => tx?.userId)
      .map((tx) => ({
        id: String(tx.id || ""),
        userId: String(tx.userId),
        label: String(
          tx.userEmail ||
            tx.email ||
            tx.userName ||
            tx.name ||
            tx.reference ||
            ""
        ),
        amount: typeof tx.amount === "number" ? tx.amount : "",
        paymentReference: tx.reference || "",
      }));
  }, [transactions]);

  useEffect(() => {
    if (!selectedTransactionId) {
      setApprovalForm((prev) => ({
        ...prev,
        userId: "",
        amount: "",
        paymentReference: "",
      }));
      return;
    }
    const selected = transactionOptions.find(
      (tx) => tx.id === selectedTransactionId
    );
    if (!selected) return;
    setApprovalForm({
      userId: selected.userId,
      amount: selected.amount,
      paymentReference: selected.paymentReference,
    });
  }, [selectedTransactionId, transactionOptions]);

  useEffect(() => {
    if (!showApproveModal || !canManage) return undefined;
    const handleKeyDown = (event) => {
      if (!showApproveModal || !canManage) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeApproveModal();
        return;
      }
      if (event.ctrlKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        userSelectRef.current?.focus();
        return;
      }
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        handleApprove();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (transactionOptions.length === 0) return;
        const delta = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex = Math.min(
          Math.max((selectedIndex === -1 ? 0 : selectedIndex + delta), 0),
          transactionOptions.length - 1
        );
        setSelectedIndex(nextIndex);
        setSelectedTransactionId(transactionOptions[nextIndex].id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    showApproveModal,
    canManage,
    handleApprove,
    selectedIndex,
    transactionOptions,
    closeApproveModal,
  ]);

  const displayTransactions = useMemo(() => transactions, [transactions]);

  return (
    <AdminLayout title="Admin Billing">
      <div className="space-y-6">
        <div className="card p-4">
          <AdminBreadcrumbs />
        </div>
        {loading ? (
          <LoadingState variant="table" rows={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchTransactions} />
        ) : displayTransactions.length === 0 ? (
          <EmptyState
            title="No billing transactions yet"
            message="There are no billing transactions to display."
            testId="admin-empty-billing"
          />
        ) : (
          <>
            <div
              className="hidden overflow-hidden rounded-lg border border-slate-200 lg:block"
              onKeyDown={handleTableKeyNav}
            >
              <table
                className="w-full text-left text-sm"
                role="table"
                aria-label="Billing transactions"
              >
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTransactions.map((tx) => (
                    <tr
                      key={tx.id || tx.reference}
                      className="border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      tabIndex={0}
                      data-row="true"
                    >
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {tx.id || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {tx.userId || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {tx.planName || tx.planId || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {typeof tx.amount === "number" ? tx.amount : "—"}{" "}
                        {tx.currency || ""}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${statusTone(
                            tx.status
                          )}`}
                        >
                          {tx.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {tx.reference || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {formatTimestamp(tx.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {tx.id ? (
                          <NavLink
                            className="text-amber-600"
                            to={`/admin/billing/transactions/${tx.id}`}
                          >
                            View detail
                          </NavLink>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 lg:hidden">
              {displayTransactions.map((tx) => (
                <div key={tx.id || tx.reference} className="card p-4 text-xs">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">
                      {tx.planName || "Billing Transaction"}
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-[10px] font-semibold ${statusTone(
                        tx.status
                      )}`}
                    >
                      {tx.status || "—"}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-slate-600">
                    <div>
                      <p className="text-slate-400">ID</p>
                      <p>{tx.id || "—"}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">User</p>
                      <p>{tx.userId || "—"}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Amount</p>
                      <p>
                        {typeof tx.amount === "number" ? tx.amount : "—"}{" "}
                        {tx.currency || ""}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400">Created</p>
                      <p>{formatTimestamp(tx.createdAt)}</p>
                    </div>
                  </div>
                  {tx.id && (
                    <NavLink
                      className="mt-3 inline-block text-xs text-amber-600"
                      to={`/admin/billing/transactions/${tx.id}`}
                    >
                      View detail
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={!canGoPrev || loading}
          >
            Prev
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!canGoNext || loading}
          >
            Next
          </button>
        </div>

        <div className="card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-900">
              Billing Approval
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowApproveModal(true)}
              disabled={!canManage}
              title={canManage ? "Open approval modal" : "Not authorized"}
              ref={approveTriggerRef}
            >
              Open Approval
            </button>
          </div>
          {!canManage && (
            <div className="mt-2 text-xs text-rose-600">Not authorized.</div>
          )}
          {approvalError && (
            <div className="mt-3">
              <div className="text-xs text-rose-600">{approvalError.message}</div>
              {approvalError.code && (
                <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                  {approvalError.code}
                </div>
              )}
            </div>
          )}
          {approvalResponse && (
            <div className="mt-3">
              <SuccessToast
                title="Approval submitted"
                message="Billing approval was submitted successfully."
                onDismiss={() => setApprovalResponse(null)}
                testId="admin-billing-approval-success"
              />
            </div>
          )}
          {approvalResponse && (
            <div className="mt-3">
              <KeyValueTable data={approvalResponse} title="Approval response" />
            </div>
          )}
        </div>

        {actionLogs.length > 0 && (
          <div className="card p-4">
            <p className="text-sm font-semibold text-slate-900">Action Log</p>
            <div className="mt-3 space-y-2 text-xs text-slate-600">
              {actionLogs.map((log, index) => (
                <div key={`${log.action}-${log.transactionId}-${index}`}>
                  {log.action} user:{log.userId || "n/a"} txn:
                  {log.transactionId || "n/a"} key:
                  {log.idempotencyKey || "n/a"} → {log.status} (apiCalls:{" "}
                  {log.apiCalls})
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AdminBlockingModal
        title="Billing credits exhausted"
        message={blockingError}
        onClose={() => setBlockingError("")}
      />

      {showApproveModal && (
        <div className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="card modal-panel w-full max-w-xl p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-billing-approve-title"
          >
            <div className="flex items-center justify-between">
              <p
                id="admin-billing-approve-title"
                className="text-sm font-semibold text-slate-900"
              >
                Approve Billing Transaction
              </p>
              <button
                className="text-xs text-slate-500"
                onClick={closeApproveModal}
                aria-label="Close approval dialog"
              >
                Esc
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Ctrl+Enter to submit, Ctrl+L to focus dropdown, ↑/↓ to navigate.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs text-slate-500">User ID</label>
                <select
                  ref={userSelectRef}
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={selectedTransactionId}
                  onChange={(event) => {
                    setSelectedTransactionId(event.target.value);
                    const nextIndex = transactionOptions.findIndex(
                      (tx) => tx.id === event.target.value
                    );
                    setSelectedIndex(nextIndex);
                  }}
                  disabled={!canManage || approvalLoading}
                >
                  <option value="">Select a transaction</option>
                  {transactionOptions.map((tx) => (
                    <option key={tx.id} value={tx.id}>
                      {tx.userId}
                      {tx.label ? ` — ${tx.label}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-500">Transaction ID</label>
                  <input
                    className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    value={selectedTransactionId || ""}
                    readOnly
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Amount</label>
                  <input
                    className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    value={approvalForm.amount}
                    readOnly
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-500">
                    Payment Reference
                  </label>
                  <input
                    className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    value={approvalForm.paymentReference}
                    readOnly
                  />
                </div>
              </div>
            </div>
            {approvalError && (
              <div className="mt-3">
                <div className="text-xs text-rose-600">{approvalError.message}</div>
                {approvalError.code && (
                  <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                    {approvalError.code}
                  </div>
                )}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="btn btn-secondary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={closeApproveModal}
                disabled={approvalLoading || approvalFlight.isRunning}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleApprove}
                disabled={approvalLoading || !canManage || approvalFlight.isRunning}
                data-testid="admin-billing-approve-btn"
              >
                {approvalLoading ? "Approving…" : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
